import { useMemo, useState } from "react";

import { useGetGamePassEligibleBets } from "../../../../../queries/game-pass/useGetGamePassEligibleBets";
import { useCasinoGameNameLabels } from "../../../../../queries/game-pass/useCasinoGameNameLabels";
import {
  collectUnresolvedCasinoGameIds,
  filterAndOrderBetsByRedeemedIds,
  mapGamePassUserBetsDataToViewModels,
  mapProgressRedeemedBetsToViewModels,
  shouldShowTaskBetPanel,
} from "../../../../../queries/game-pass/taskBetMappers";
import type {
  GamePassTaskBetViewModel,
  GamePassUserBet,
} from "../../../../../queries/game-pass/taskBetTypes";
import type {
  GamePassUserProgressTask,
  TaskListRecord,
} from "../../../../../types/gamePass";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../../utils/ErrorUtils";

import "./stylesTaskBetPanel.css";

const DEFAULT_VISIBLE_BET_COUNT = 5;

type TaskBetPanelProps = {
  taskId: string;
  userId?: string;
  task?: TaskListRecord | null;
  progressTask?: GamePassUserProgressTask | null;
  open: boolean;
};

const TaskBetDetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <div className="user-manager-game-pass-task-bet-detail-row">
    <span className="user-manager-game-pass-task-bet-detail-label">
      {label}:
    </span>
    <span className="user-manager-game-pass-task-bet-detail-value">{value}</span>
  </div>
);

const TaskBetDetailsCard = ({ bet }: { bet: GamePassTaskBetViewModel }) => (
  <div className="user-manager-game-pass-task-bet-details-card">
    <TaskBetDetailRow label={AppStrings.BET_ID} value={bet.betId} />
    {bet.betKind === "casino" ? (
      <>
        <TaskBetDetailRow
          label={AppStrings.USER_GAME_PASS_TASK_BET_GAME_TYPE}
          value={bet.gameTypeLabel}
        />
        <TaskBetDetailRow
          label={AppStrings.USER_GAME_PASS_TASK_BET_GAME}
          value={bet.gameNameLabel}
        />
        <TaskBetDetailRow
          label={AppStrings.USER_GAME_PASS_TASK_BET_AMOUNT}
          value={bet.betAmountLabel}
        />
      </>
    ) : (
      <>
        <TaskBetDetailRow label={AppStrings.SPORT} value={bet.sportLabel} />
        <TaskBetDetailRow
          label={AppStrings.SELECTION}
          value={bet.selectionLabel}
        />
        <TaskBetDetailRow label={AppStrings.ODDS} value={bet.oddsLabel} />
      </>
    )}
    <TaskBetDetailRow
      label={AppStrings.USER_GAME_PASS_TASK_BET_RESULT}
      value={bet.resultLabel}
    />
    <TaskBetDetailRow
      label={AppStrings.USER_GAME_PASS_TASK_BET_DATE}
      value={bet.placedDateLabel}
    />
  </div>
);

const TaskBetSubmittedSection = ({ bet }: { bet: GamePassTaskBetViewModel }) => (
  <div className="user-manager-game-pass-task-bet-submitted-section">
    <h4 className="user-manager-game-pass-task-bet-submitted-section-title">
      {AppStrings.USER_GAME_PASS_TASK_BETS_SUBMITTABLE}
    </h4>
    <div className="user-manager-game-pass-task-bet-submitted-card">
      <div className="user-manager-game-pass-task-bet-submitted-card-body">
        <div className="user-manager-game-pass-task-bet-submitted-card-main">
          <div className="user-manager-game-pass-task-bet-submitted-card-line">
            <span className="user-manager-game-pass-task-bet-submitted-card-line-label">
              {AppStrings.BET_ID}:
            </span>
            <span className="user-manager-game-pass-task-bet-submitted-card-line-value">
              {bet.betId}
            </span>
          </div>
          <div className="user-manager-game-pass-task-bet-submitted-card-description">
            {bet.submittedDescription}
          </div>
        </div>

        <div className="user-manager-game-pass-task-bet-submitted-card-side">
          <span
            className={`user-manager-game-pass-task-bet-submitted-card-highlight user-manager-game-pass-task-bet-submitted-card-highlight-${bet.resultVariant}`}
            data-testid={`game-pass-task-bet-highlight-${bet.betId}`}
          >
            {bet.resultVariant === "lost" && bet.betKind === "casino"
              ? bet.betAmountLabel
              : bet.submittedHighlightLabel}
          </span>
          <span className="user-manager-game-pass-task-bet-submitted-card-date">
            {bet.submittedDateLabel}
          </span>
        </div>

        <div className="user-manager-game-pass-task-bet-submitted-card-status">
          <span
            className={`user-manager-game-pass-task-bet-result-pill user-manager-game-pass-task-bet-result-pill-${bet.resultVariant}`}
          >
            {bet.resultLabel}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const TaskBetRow = ({ bet }: { bet: GamePassTaskBetViewModel }) => (
  <div
    className="user-manager-game-pass-task-bet-row"
    data-testid={`game-pass-task-bet-row-${bet.betId}`}
  >
    <TaskBetDetailsCard bet={bet} />
    <TaskBetSubmittedSection bet={bet} />
  </div>
);

const TaskBetPanel = ({
  taskId,
  userId,
  task,
  progressTask,
  open,
}: TaskBetPanelProps) => {
  const [showAllBets, setShowAllBets] = useState(false);
  const redeemedBetIds = useMemo(
    () => progressTask?.redeemed_bet_ids ?? [],
    [progressTask?.redeemed_bet_ids],
  );
  const hasRedeemedBetIds = redeemedBetIds.length > 0;
  const taskDataSource = task?.data_source ?? undefined;
  const shouldFetchBets =
    open &&
    Boolean(userId) &&
    Boolean(taskId) &&
    shouldShowTaskBetPanel(task) &&
    hasRedeemedBetIds;
  const betsQuery = useGetGamePassEligibleBets(userId, {
    enabled: shouldFetchBets,
    taskId,
  });

  const unresolvedCasinoGameIds = useMemo(() => {
    const rawBets: GamePassUserBet[] = [
      ...(betsQuery.data?.data?.bets ?? []),
      ...(progressTask?.redeemed_bets ?? []),
    ];
    return collectUnresolvedCasinoGameIds(rawBets, taskDataSource);
  }, [betsQuery.data?.data?.bets, progressTask?.redeemed_bets, taskDataSource]);
  const casinoGameNameLabels = useCasinoGameNameLabels(unresolvedCasinoGameIds);

  const taskBets = useMemo(() => {
    if (!hasRedeemedBetIds) {
      return [];
    }

    const betsFromEligible = filterAndOrderBetsByRedeemedIds(
      mapGamePassUserBetsDataToViewModels(betsQuery.data?.data, {
        taskDataSource,
        casinoGameNameLabels,
      }),
      redeemedBetIds,
    );
    if (betsFromEligible.length > 0) {
      return betsFromEligible;
    }

    return mapProgressRedeemedBetsToViewModels(
      progressTask?.redeemed_bets,
      redeemedBetIds,
      { taskDataSource, casinoGameNameLabels },
    );
  }, [
    betsQuery.data?.data,
    casinoGameNameLabels,
    hasRedeemedBetIds,
    progressTask?.redeemed_bets,
    redeemedBetIds,
    taskDataSource,
  ]);

  const renderBetList = (allBets: ReadonlyArray<GamePassTaskBetViewModel>) => {
    const hasHiddenBets = allBets.length > DEFAULT_VISIBLE_BET_COUNT;
    const visibleBets = showAllBets
      ? allBets
      : allBets.slice(0, DEFAULT_VISIBLE_BET_COUNT);

    return (
      <div
        className="user-manager-game-pass-task-panel user-manager-game-pass-task-bet-panel"
        data-testid={`game-pass-task-panel-${taskId}`}
      >
        <div className="user-manager-game-pass-task-bet-list">
          {visibleBets.map((bet) => (
            <TaskBetRow key={bet.betId} bet={bet} />
          ))}
        </div>
        {hasHiddenBets ? (
          <button
            className="user-manager-game-pass-task-bet-toggle"
            data-testid={`game-pass-task-bet-toggle-${taskId}`}
            type="button"
            onClick={() => setShowAllBets((value) => !value)}
          >
            {showAllBets
              ? AppStrings.USER_GAME_PASS_TASK_BETS_SEE_LESS
              : AppStrings.USER_GAME_PASS_TASK_BETS_SEE_MORE}
          </button>
        ) : null}
      </div>
    );
  };

  const renderEmptyPanel = () => (
    <div
      className="user-manager-game-pass-task-panel"
      data-testid={`game-pass-task-panel-${taskId}`}
    >
      {AppStrings.USER_GAME_PASS_TASK_ACCORDION_EMPTY}
    </div>
  );

  if (!shouldShowTaskBetPanel(task) || !hasRedeemedBetIds) {
    return renderEmptyPanel();
  }

  if (betsQuery.isLoading || betsQuery.isFetching) {
    return (
      <div
        className="user-manager-game-pass-task-panel"
        data-testid={`game-pass-task-panel-${taskId}`}
      >
        {AppStrings.USER_GAME_PASS_TASK_BETS_LOADING}
      </div>
    );
  }

  if (betsQuery.isError) {
    const fallbackBets = mapProgressRedeemedBetsToViewModels(
      progressTask?.redeemed_bets,
      redeemedBetIds,
      { taskDataSource, casinoGameNameLabels },
    );
    if (fallbackBets.length > 0) {
      return renderBetList(fallbackBets);
    }

    return (
      <div
        className="user-manager-game-pass-task-panel"
        data-testid={`game-pass-task-panel-${taskId}`}
      >
        {getApiErrorMessage(
          betsQuery.error,
          AppStrings.USER_GAME_PASS_TASK_ACCORDION_EMPTY,
        )}
      </div>
    );
  }

  if (taskBets.length === 0) {
    return renderEmptyPanel();
  }

  return renderBetList(taskBets);
};

export default TaskBetPanel;
